import { useMemo } from 'react';
import { useQuery } from 'urql';

import { SKILL_TAXONOMY_SECONDARY_SELECTION_LIST_QUERY } from '@revelio/data-access';
import { SelectionCategories, type SelectionList } from '@revelio/iso-utility';

import { removeBadSkills } from '../../../shared/selection-lists/utils/removeBadSkills';
import { isV3Taxonomy } from '../utils/job-taxonomy-version-migration';

const V3_SKILL_CATEGORY_TO_LEVEL_ID: Partial<
  Record<SelectionCategories, number>
> = {
  [SelectionCategories.SKILL_K150]: 1,
  [SelectionCategories.SKILL_K1500]: 2,
  [SelectionCategories.SKILL_K5000]: 3,
  [SelectionCategories.SKILL_K15000]: 4,
  [SelectionCategories.SKILL_K35000]: 5,
};

const V3_SKILL_PARENT_OF: Partial<
  Record<SelectionCategories, SelectionCategories>
> = {
  [SelectionCategories.SKILL_K1500]: SelectionCategories.SKILL_K150,
  [SelectionCategories.SKILL_K5000]: SelectionCategories.SKILL_K1500,
  [SelectionCategories.SKILL_K15000]: SelectionCategories.SKILL_K5000,
  [SelectionCategories.SKILL_K35000]: SelectionCategories.SKILL_K15000,
};
/**
 * Fetches skill selection lists for Data Builder.
 * Uses custom skill taxonomy (v3) when enabled.
 */
export const useSkillSelectionLists = (
  requiredSkillLists: string[] = [],
  options: { taxonomyId?: number; skillTaxonomyVersion?: string }
) => {
  const isV3 = isV3Taxonomy(options.skillTaxonomyVersion);
  const taxonomyId = options.taxonomyId ?? 0;

  const [{ data: secondaryData, fetching: skillsFetching }] = useQuery({
    query: SKILL_TAXONOMY_SECONDARY_SELECTION_LIST_QUERY,
    variables: {
      taxonomy_id: taxonomyId,
      l0: 1,
      l1: 2,
      l2: 3,
      l3: 4,
      l4: 5,
    },
    pause: !isV3,
  });

  const transformedSkillLists = useMemo(() => {
    const result: Record<string, SelectionList> = {};
    if (!isV3) return result;

    const sel = secondaryData?.selectionList;
    const makeItems = (node?: {
      refs?: Array<{
        id: number | string;
        short_name: string;
        long_name: string;
        parents?: number[];
      }>;
    }) =>
      removeBadSkills(node?.refs).map((r) => ({
        id: String(r.id),
        shortName: r.short_name,
        longName: r.long_name,
        label: r.long_name || r.short_name,
        parentIds: (r.parents || []).map((p) => String(p)),
      }));

    const levelToItems: Record<number, SelectionList['value']> = {
      1: makeItems(sel?.skill_0),
      2: makeItems(sel?.skill_1),
      3: makeItems(sel?.skill_2),
      4: makeItems(sel?.skill_3),
      5: makeItems(sel?.skill_4),
    };

    requiredSkillLists.forEach((listId) => {
      const lvl = V3_SKILL_CATEGORY_TO_LEVEL_ID[listId as SelectionCategories];
      if (typeof lvl !== 'number') return;
      const parentCategory = V3_SKILL_PARENT_OF[listId as SelectionCategories];
      const immediateParentIndex = lvl - 2;
      const items = (levelToItems[lvl] || []).map(
        (
          i: {
            parentIds?: string[];
          } & SelectionList['value'][number]
        ) => {
          if (parentCategory) {
            const chain = i.parentIds ?? [];
            const immediate =
              immediateParentIndex >= 0 && immediateParentIndex < chain.length
                ? chain[immediateParentIndex]
                : undefined;
            return {
              ...i,
              [parentCategory]: immediate !== undefined ? [immediate] : [],
            };
          }
          return i;
        }
      );
      result[listId] = {
        id: listId as SelectionCategories,
        label: listId as SelectionCategories,
        value: items,
      };
    });

    return result;
  }, [isV3, requiredSkillLists, secondaryData]);

  return {
    skillSelectionLists: transformedSkillLists,
    isV3Taxonomy: isV3,
    taxonomyId,
    isLoading: skillsFetching,
  };
};
